import BlockType from '../../../../../enums/blockType';
import EdgeLabel from '../../../../../enums/edgeLabel';
import EdgeType from '../../../../../enums/edgeType';
import { Block, Edge, Segment } from '../../../../../types/shared/blocksV3';
import { getOppositeEdge } from './edgeUtils';
import { v4 as uuidv4 } from 'uuid';
import getVacantSegment from './getVacantSegment';
import validateSelectedVacantSegment from './validateSelectedVacantSegment';
import { MIN_LANDING_WIDTH } from '../../../../../constants/limits';

export function getDefaultLanding({
	blocks,
	parentBlock,
	selectedBlockEdge,
}: {
	blocks: Block[];
	parentBlock: Block | null;
	selectedBlockEdge: Edge | undefined;
}) {
	if (!parentBlock || !selectedBlockEdge) return;

	const defaultSize = 48;

	const vacantSegment = getVacantSegment({
		blocks,
		parentBlock,
		selectedBlockEdge,
		defaultSize,
	});

	if (!vacantSegment) return;

	const updatedOldSegment: Segment = {
		...vacantSegment,
		length: defaultSize,
		type: EdgeType.LANDING_ATTACHED,
	};

	let newSegment: Segment | null = null;

	if (vacantSegment.length > defaultSize) {
		newSegment = {
			id: uuidv4(),
			edgeId: selectedBlockEdge.id,
			blockId: vacantSegment.blockId,
			length: vacantSegment.length - defaultSize,
			type: vacantSegment.type,
		};
	}

	const defaultLanding = constructLanding({
		parentBlock,
		selectedBlockEdge,
		vacantSegment: updatedOldSegment,
	});

	return { defaultLanding, newSegment, updatedOldSegment };
}

export function getDefaultLandingForSegment({
	parentBlock,
	selectedBlockEdge,
	selectedBlockSegment,
}: {
	parentBlock: Block | null;
	selectedBlockEdge: Edge | undefined;
	selectedBlockSegment: Segment;
}) {
	if (!parentBlock || !selectedBlockEdge) return;

	if (!validateSelectedVacantSegment(selectedBlockSegment, MIN_LANDING_WIDTH)) {
		return;
	}

	const segmentLength = selectedBlockSegment.length;

	const updatedOldSegment: Segment = {
		...selectedBlockSegment,
		type: EdgeType.LANDING_ATTACHED,
	};

	const defaultLanding = constructLanding({
		parentBlock,
		selectedBlockEdge,
		vacantSegment: selectedBlockSegment,
	});

	return {
		defaultLanding,
		updatedOldSegment,
		newSegment: null,
	};
}

export default getDefaultLanding;

export function constructLanding({
	parentBlock,
	selectedBlockEdge,
	vacantSegment,

	id: existingId,
	label,
}: {
	parentBlock: Block;
	selectedBlockEdge: Edge;
	vacantSegment: Segment;

	id?: string;
	label?: string;
}): Block {
	const id = existingId || uuidv4();
	const inferedHeight = (() => {
		if (parentBlock.type !== BlockType.STAIR) {
			return parentBlock?.dimensions?.height || 0;
		}
		const stairAttachedEdge = parentBlock.edges.find((e) => e.attachedToParent);
		const endEdgeLabel = stairAttachedEdge
			? getOppositeEdge(stairAttachedEdge.label)
			: null;
		if (selectedBlockEdge.label === endEdgeLabel) {
			// Use endHeight if it's a valid number (including 0), otherwise fall back
			return parentBlock.dimensions.endHeight != null
				? parentBlock.dimensions.endHeight
				: parentBlock.dimensions.startHeight ||
						parentBlock.dimensions.height ||
						0;
		}
		return (
			parentBlock.dimensions.startHeight || parentBlock.dimensions.height || 0
		);
	})();

	const isHorizontalEdge =
		selectedBlockEdge.label == EdgeLabel.TOP ||
		selectedBlockEdge.label == EdgeLabel.BOTTOM;

	const segmentLength = vacantSegment.length;

	const parentDimensions = parentBlock.dimensions;
	const isParentStair = parentBlock.type === BlockType.STAIR;

	const landingDepth = isHorizontalEdge
		? isParentStair
			? parentDimensions.depth || MIN_LANDING_WIDTH
			: 48
		: isParentStair
			? parentDimensions.width || MIN_LANDING_WIDTH
			: 48;

	const defaultLanding: Block = {
		id,
		label: label || 'Landing',
		type: BlockType.LANDING,
		dimensions: {
			width: isHorizontalEdge ? segmentLength : landingDepth,
			depth: isHorizontalEdge ? landingDepth : segmentLength,
			height: inferedHeight,
		},
		displayDimensions: {
			width: isHorizontalEdge ? segmentLength : landingDepth,
			height: isHorizontalEdge ? landingDepth : segmentLength,
		},
		parentBlock: {
			id: vacantSegment.blockId,
			type: parentBlock.type,
			edgeId: selectedBlockEdge.id,
			segmentId: vacantSegment.id,
		},
		edges: [
			EdgeLabel.TOP,
			EdgeLabel.RIGHT,
			EdgeLabel.BOTTOM,
			EdgeLabel.LEFT,
		].map((edge) => {
			const edgeId = uuidv4();
			const edgeLength =
				edge === selectedBlockEdge.label ||
				edge === getOppositeEdge(selectedBlockEdge.label)
					? segmentLength
					: landingDepth;

			const newEdge = {
				label: edge,
				id: edgeId,
				length: edgeLength,
				attachedToParent: edge === getOppositeEdge(selectedBlockEdge.label),
				segments: [
					{
						id: uuidv4(),
						edgeId,
						blockId: id,
						length: edgeLength,
						type:
							edge === selectedBlockEdge.label
								? inferedHeight == 0
									? EdgeType.GROUND
									: EdgeType.RAILING
								: edge === getOppositeEdge(selectedBlockEdge.label)
									? EdgeType.BLOCK_ATTACHED
									: EdgeType.RAILING,
					},
				],
			};

			return newEdge;
		}),
	};
	return defaultLanding;
}
